import { useState, useEffect, useRef } from "react";
import { Camera, Shield, Fingerprint, ChevronDown, Search } from "lucide-react";

export default function MockupSawblade() {
  const [scrollY, setScrollY] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => setScrollY(container.scrollTop);
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div ref={containerRef} className="h-screen overflow-y-auto bg-[#111827] text-white" style={{ scrollBehavior: "smooth" }}>
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-4" style={{ backgroundColor: scrollY > 50 ? "rgba(17,24,39,0.95)" : "transparent", backdropFilter: scrollY > 50 ? "blur(12px)" : "none", transition: "all 0.3s" }}>
        <img src="/logo-sawblade.png" alt="TOOLIO" className="h-10" />
        <button className="text-yellow-400 text-sm font-bold tracking-wide">SIGN IN</button>
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-24 pb-16 overflow-hidden">
        <div className="absolute top-20 right-0 w-[300px] h-[300px] bg-yellow-500/5 rounded-full blur-3xl" style={{ transform: `translateY(${scrollY * -0.2}px)` }} />
        
        <div className="relative z-10">
          <div className="inline-block bg-yellow-500/10 border border-yellow-500/20 rounded-full px-3 py-1 mb-6">
            <span className="text-yellow-500 text-xs font-bold tracking-wider">AI-POWERED TOOL REGISTRY</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-black tracking-tight leading-[0.95] mb-2">
            YOUR<br/>TOOLS.
          </h1>
          <h1 className="text-5xl sm:text-6xl font-black tracking-tight leading-[0.95] text-yellow-500 mb-6">
            YOUR<br/>PROOF.
          </h1>

          <p className="text-gray-400 text-base max-w-xs leading-relaxed mb-10">
            Register in seconds. Prove ownership forever. The UK's most trusted tool protection platform.
          </p>

          <button className="bg-yellow-500 text-black font-bold px-8 py-4 rounded-sm text-sm tracking-wider w-full mb-3">REGISTER FREE</button>
          <button className="border border-white/10 text-white font-medium px-8 py-4 rounded-sm text-sm tracking-wider w-full flex items-center justify-center gap-2">
            <Search className="w-4 h-4" /> CHECK A TOOL
          </button>
        </div>

        <ChevronDown className="absolute bottom-8 left-1/2 -translate-x-1/2 w-6 h-6 text-yellow-500/60 animate-bounce" />
      </section>

      {/* Dashboard Preview with tilt */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#111827] via-[#0d1320] to-[#111827]" />
        <div className="relative z-10">
          <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">The Dashboard</p>
          <h2 className="text-2xl font-bold text-center mb-2">Complete visibility</h2>
          <p className="text-gray-500 text-sm text-center mb-10">Track every tool. See every status.</p>
          
          <div className="relative mx-auto max-w-sm" style={{ transform: `perspective(1000px) rotateX(${Math.min(5, Math.max(-5, (scrollY - 600) * 0.02))}deg)`, transition: "transform 0.1s" }}>
            <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl">
              <img src="/dashboard-preview.png" alt="TOOLIO Dashboard" className="w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Features - Cards */}
      <section className="py-20 px-6">
        <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-3 text-center">Features</p>
        <h2 className="text-2xl font-bold text-center mb-10">Built for the trade</h2>

        <div className="space-y-4">
          {[
            { icon: <Camera className="w-5 h-5" />, title: "AI Label Scanning", desc: "Point your camera, and our AI reads manufacturer, model, serial — instantly." },
            { icon: <Fingerprint className="w-5 h-5" />, title: "Unique Tool ID", desc: "Every registered tool gets an immutable NanoID. Your digital proof of ownership." },
            { icon: <Shield className="w-5 h-5" />, title: "Police Connected", desc: "Report stolen tools. Police forces can look up any tool in seconds." },
            { icon: <Search className="w-5 h-5" />, title: "Public Verification", desc: "Buyers can check if a tool is stolen before purchase. Stops thieves reselling." }
          ].map((item, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-md bg-yellow-500/10 flex items-center justify-center text-yellow-500">{item.icon}</div>
                <h3 className="font-bold text-sm">{item.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed pl-11">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16 px-6 bg-[#0a1020]">
        <div className="text-center mb-8">
          <div className="text-4xl font-black text-yellow-500 mb-1">10,000+</div>
          <div className="text-sm text-gray-500">Tools registered and counting</div>
        </div>
        <div className="flex justify-center gap-8">
          {["Police Verified", "AI Powered", "Instant Scan"].map((t, i) => (
            <div key={i} className="text-center">
              <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500" />
              </div>
              <div className="text-xs text-gray-500">{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 text-center">
        <h2 className="text-3xl font-black mb-3">Ready to protect<br/>your tools?</h2>
        <p className="text-gray-400 text-sm mb-8">Free to register. Takes under a minute.</p>
        <button className="bg-yellow-500 text-black font-bold px-8 py-4 rounded-sm text-sm tracking-wider w-full max-w-xs">GET STARTED</button>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5 text-center">
        <img src="/logo-sawblade.png" alt="TOOLIO" className="h-8 mx-auto mb-3" />
        <p className="text-xs text-gray-600">tool-io.uk</p>
      </footer>
    </div>
  );
}
